import React, { useState, useEffect, useRef, useCallback, useReducer, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import './GameScreen.css';
import { fetchNewChallenge, submitPosition, finalizeGameSession, fetchMapData, fetchUserSettings, fetchSounds, clearApiCache } from '../services/apiService';
import { useFocusTrap } from '../hooks';
import ReportModal from './ReportModal';
import Advertisement from './Advertisement';
import confetti from 'canvas-confetti';
import { ZoomableMap, GuessMarker, ActualMarker, DistanceLine } from './ZoomableMap';

// ===== REDUCERS =====

// Modal state reducer - groups all modal-related states
const modalReducer = (state, action) => {
  switch (action.type) {
    case 'OPEN_MAP_MODAL':
      return { ...state, showMapModal: true, tempSelectedMap: action.payload };
    case 'SET_INLINE_MAP':
      return { ...state, showMapModal: false, tempSelectedMap: action.payload };
    case 'CLOSE_MAP_MODAL':
      return { ...state, showMapModal: false, tempSelectedMap: '' };
    case 'SHOW_ERROR':
      return { ...state, showErrorModal: true, errorMessage: action.payload.message, errorCallback: action.payload.callback };
    case 'CLOSE_ERROR':
      return { ...state, showErrorModal: false, errorMessage: '', errorCallback: null };
    case 'SHOW_FINAL_SCORE':
      return { ...state, showFinalScoreModal: true };
    case 'CLOSE_FINAL_SCORE':
      return { ...state, showFinalScoreModal: false };
    default:
      return state;
  }
};

// Loading flags reducer - groups all loading state flags
const loadingFlagsReducer = (state, action) => {
  switch (action.type) {
    case 'SET_USER_SETTINGS_LOADED':
      return { ...state, userSettings: true };
    case 'SET_SOUNDS_LOADED':
      return { ...state, sounds: true };
    case 'SET_CHALLENGE_LOADED':
      return { ...state, challenge: action.payload };
    case 'SET_MAP_DATA_LOADED':
      return { ...state, mapData: true };
    case 'RESET_ALL':
      return { userSettings: false, sounds: false, challenge: false, mapData: false };
    default:
      return state;
  }
};

// Initial states
const initialModalState = { showMapModal: false, tempSelectedMap: '', showErrorModal: false, errorMessage: '', errorCallback: null, showFinalScoreModal: false };
const initialLoadingFlags = { userSettings: false, sounds: false, challenge: false, mapData: false };

const GameScreen = ({ gameState, updateGameState, onGameEnd, user, globalMuted, setGlobalMuted, globalVolume, setGlobalVolume }) => {
  const { t } = useTranslation();

  // ===== REDUCERS =====
  const [modalState, modalDispatch] = useReducer(modalReducer, initialModalState);
  const [loadingFlags, loadingDispatch] = useReducer(loadingFlagsReducer, initialLoadingFlags);

  // Destructure for easier access
  const { showMapModal, tempSelectedMap, showErrorModal, errorMessage, errorCallback, showFinalScoreModal } = modalState;

  // ===== SIMPLE STATES =====
  // Game state
  const [loading, setLoading] = useState(true);
  const [selectedMap, setSelectedMap] = useState('');
  const [selectedPosition, setSelectedPosition] = useState(null);
  const [selectedPositionRelative, setSelectedPositionRelative] = useState(null);
  const [roundResult, setRoundResult] = useState(null);
  const [timeRemaining, setTimeRemaining] = useState(60);
  const [mapData, setMapData] = useState({});
  const [showResults, setShowResults] = useState(false);
  const [timeExpired, setTimeExpired] = useState(false);
  const [timerAlertPlayed, setTimerAlertPlayed] = useState(false);

  // Sound state
  const [selectedTimerAlert, setSelectedTimerAlert] = useState(null);
  const [disableSoundAlerts, setDisableSoundAlerts] = useState(false);
  const [sounds, setSounds] = useState([]);

  // Layout preference
  const [gameLayout, setGameLayout] = useState(user?.game_layout || 'split');

  // Report modal state
  const [showReportModal, setShowReportModal] = useState(false);

  // ===== MEMOIZED VALUES =====
  // Total score calculation - used in multiple places
  const totalScore = useMemo(() => {
    return gameState.scores.reduce((sum, score) => sum + score, 0);
  }, [gameState.scores]);

  // Map keys for iteration
  const mapKeys = useMemo(() => Object.keys(mapData), [mapData]);

  // Wrapper for onGameEnd
  const handleGameEnd = useCallback(() => {
    // Reset all loading flags to ensure fresh data on next game start
    loadingDispatch({ type: 'RESET_ALL' });

    // Clear API cache to ensure fresh data on next game start
    clearApiCache();

    // Call the original onGameEnd function
    onGameEnd();
  }, [onGameEnd]);

  // Confetti celebration for high scores
  const triggerConfetti = useCallback((score) => {
    if (score >= 4500) {
      confetti({
        particleCount: 100,
        spread: 70,
        origin: { y: 0.6 },
        colors: ['#ff9c00', '#ffb84d', '#ffd700', '#ff6b00', '#ffffff']
      });
    } else if (score >= 3000) {
      // Smaller celebration for good scores
      confetti({
        particleCount: 50,
        spread: 45,
        origin: { y: 0.6 },
        colors: ['#ff9c00', '#ffb84d']
      });
    }
  }, []);

  // Refs for DOM elements
  const timerRef = useRef(null);
  const timerAlertAudioRef = useRef(null);

  // Focus trap refs for modals (accessibility)
  const mapModalFocusTrapRef = useFocusTrap(showMapModal, {
    onEscape: () => modalDispatch({ type: 'CLOSE_MAP_MODAL' }),
    autoFocus: true,
    restoreFocus: true
  });

  const errorModalFocusTrapRef = useFocusTrap(showErrorModal, {
    onEscape: () => modalDispatch({ type: 'CLOSE_ERROR' }),
    autoFocus: true,
    restoreFocus: true
  });

  const finalScoreModalFocusTrapRef = useFocusTrap(showFinalScoreModal, {
    onEscape: handleGameEnd,
    autoFocus: true,
    restoreFocus: true
  });

  // Handle position selection on map
  const handlePositionSelect = useCallback((relativePos) => {
    setSelectedPositionRelative(relativePos);
    setSelectedPosition(relativePos); // Now stores relative (0-1) coords too
  }, []);

  // Load map data when component mounts
  useEffect(() => {
    const loadMapData = async () => {
      if (loadingFlags.mapData) return;

      try {
        const maps = await fetchMapData();
        setMapData(maps);
        loadingDispatch({ type: 'SET_MAP_DATA_LOADED' });
      } catch (error) {
        console.error('Error loading map data:', error);
      }
    };

    loadMapData();
  }, [loadingFlags.mapData]);

  // Load user settings when component mounts
  useEffect(() => {
    const loadUserSettings = async () => {
      if (loadingFlags.userSettings) return;

      try {
        const settings = await fetchUserSettings();
        setSelectedTimerAlert(settings.selected_timer_alert || null);
        setDisableSoundAlerts(settings.disable_sound_alerts || false);
        setGameLayout(settings.game_layout || 'split');
        loadingDispatch({ type: 'SET_USER_SETTINGS_LOADED' });
      } catch (error) {
        console.error('Error loading user settings:', error);
      }
    };

    loadUserSettings();
  }, [loadingFlags.userSettings]);

  // Load sounds when component mounts
  useEffect(() => {
    const loadSounds = async () => {
      if (loadingFlags.sounds) return;

      try {
        const soundsData = await fetchSounds();
        setSounds(soundsData);
        loadingDispatch({ type: 'SET_SOUNDS_LOADED' });
      } catch (error) {
        console.error('Error loading sounds:', error);
      }
    };

    loadSounds();
  }, [loadingFlags.sounds]);

  // Load challenge when component mounts or round changes
  // Both normal and daily challenge modes use the same /api/challenge endpoint.
  // Daily challenges pass dailyChallengeId so the server returns predetermined points.
  useEffect(() => {
    const loadChallenge = async () => {
      if (loadingFlags.challenge && gameState.challengeId) return;

      setLoading(true);
      try {
        const challenge = await fetchNewChallenge(
          gameState.difficulty,
          gameState.sessionId,
          gameState.gamemode,
          gameState.dailyChallengeId || null
        );

        updateGameState({
          challengeId: challenge.id,
          challengeImage: challenge.image,
          sessionId: challenge.sessionId,
          currentPointId: challenge.pointId,
          currentMapId: challenge.mapId
        });

        loadingDispatch({ type: 'SET_CHALLENGE_LOADED', payload: true });
        setLoading(false);
      } catch (error) {
        console.error('Error loading challenge:', error);

        if (error.response && error.response.status === 403 &&
            error.response.data && error.response.data.error === 'daily_limit_reached') {
          showError(t('game.dailyLimitReached', 'You have reached the daily limit of games. Register with BattleNet to play unlimited games!'), onGameEnd);
        }

        setLoading(false);
      }
    };

    if (gameState.currentRound > 0 && !gameState.challengeId) {
      loadChallenge();
    }
  }, [gameState.currentRound, gameState.difficulty, gameState.sessionId, gameState.gamemode, updateGameState, gameState.challengeId, onGameEnd, t, loadingFlags.challenge, gameState.dailyChallengeId]);

  // Function to handle time expiration - defined outside useEffect to avoid circular dependency
  const handleTimeExpiration = useCallback(() => {
    // Clear the interval
    if (timerRef.current) {
      clearInterval(timerRef.current);
    }

    // Close map modal if it's open
    if (showMapModal) {
      modalDispatch({ type: 'CLOSE_MAP_MODAL' });
    }

    // We'll set a flag to indicate time expiration
    // The actual submission will happen in the handleSubmit function
    setTimeExpired(true);

    // Submit with default values
    const defaultMap = mapKeys[0] || '';
    const defaultPosition = { x: 0.5, y: 0.5 }; // Center in 0-1 range

    // Update state for UI consistency
    setSelectedMap(defaultMap);
    setSelectedPositionRelative(defaultPosition);

    // Trigger submission with the default map
    if (defaultMap) {
      // Get map dimensions from mapData
      const mapWidth = mapData[defaultMap]?.width || 100;
      const mapHeight = mapData[defaultMap]?.height || 100;

      // Convert relative coordinates to absolute pixel coordinates
      const absolutePosition = { 
        x: defaultPosition.x * mapWidth, 
        y: defaultPosition.y * mapHeight 
      };

      // Submit the position directly to the API (time expired, so timeRemaining is 0)
      submitPosition(
        gameState.challengeId,
        defaultMap,
        absolutePosition,
        gameState.sessionId,
        0
      ).then(result => {
        // Create a result object with 0 points
        const timeExpiredResult = {
          score: 0,
          distance: -1, // -1 to hide the line and actual position
          timeExpired: true
        };

        // Update scores in game state with 0 points
        const updatedScores = [...gameState.scores, 0];
        updateGameState({ scores: updatedScores });

        // Set round result and show results
        setRoundResult(timeExpiredResult);
        setShowResults(true);
      }).catch(error => {
        console.error('Error submitting position after time expired:', error);
        showError(t('game.errorSubmittingPosition', 'Error submitting position. Please try again.'));
      });
    }

    return 0;
  }, [
    timerRef, 
    showMapModal, 
    mapData, 
    gameState.challengeId, 
    gameState.sessionId, 
    gameState.scores, 
    t, 
    updateGameState
  ]);

  // Set up timer
  useEffect(() => {
    // Don't start timer if loading, showing results, or no map data available
    if (loading || showResults || mapKeys.length === 0) return;

    // Reset timer alert played state when timer starts
    setTimerAlertPlayed(false);

    // Function to play timer alert sound
    const playTimerAlert = () => {
      // Stop any previous audio before playing new one
      if (timerAlertAudioRef.current) {
        timerAlertAudioRef.current.pause();
        timerAlertAudioRef.current = null;
      }

      // If sound alerts are disabled or session is muted, don't play any sound
      if (disableSoundAlerts || globalMuted) {
        setTimerAlertPlayed(true);
        return;
      }

      try {
        let audio;

        // Use selected timer alert sound if available, otherwise use default
        if (selectedTimerAlert) {
          const sound = sounds.find(s => s.code === selectedTimerAlert);
          if (sound) {
            audio = new Audio(sound.path);
          }
        }

        // Fallback to default sound if no selection or selected sound not found
        if (!audio) {
          audio = new Audio('/sounds/timer_alert.mp3');
        }

        audio.volume = globalVolume / 100; // Set volume based on globalVolume (0-100 to 0-1)
        timerAlertAudioRef.current = audio; // Store reference for later control
        audio.play().catch(error => {
          console.error('Error playing timer alert sound:', error);
        });
        setTimerAlertPlayed(true);
      } catch (error) {
        console.error('Error creating timer alert sound:', error);
      }
    };


    // Set up the interval for the timer
    timerRef.current = setInterval(() => {
      setTimeRemaining(prev => {
        // When timer reaches 10 seconds
        if (prev === 10) {
          // Play timer alert sound
          playTimerAlert();
        }

        // When timer reaches 0
        if (prev <= 1) {
          return handleTimeExpiration();
        }

        // Normal countdown
        return prev - 1;
      });
    }, 1000);

    // Cleanup function
    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
      // Stop audio if playing
      if (timerAlertAudioRef.current) {
        timerAlertAudioRef.current.pause();
        timerAlertAudioRef.current = null;
      }
    };
  }, [loading, showResults, mapData, showMapModal, sounds, handleTimeExpiration, selectedTimerAlert, disableSoundAlerts, globalMuted, globalVolume]);

  // Stop timer alert audio when muted
  useEffect(() => {
    if (globalMuted && timerAlertAudioRef.current) {
      timerAlertAudioRef.current.pause();
      timerAlertAudioRef.current = null;
    }
  }, [globalMuted]);

  // Handle map selection
  const handleMapSelect = useCallback((mapId) => {
    setSelectedPosition(null);
    setSelectedPositionRelative(null);
    if (gameLayout === 'modal') {
      modalDispatch({ type: 'OPEN_MAP_MODAL', payload: mapId });
    } else {
      modalDispatch({ type: 'SET_INLINE_MAP', payload: mapId });
    }
  }, [gameLayout]);

  // Handle modal cancellation
  const handleModalCancel = useCallback(() => {
    modalDispatch({ type: 'CLOSE_MAP_MODAL' });
  }, []);

  // Handle modal confirmation
  const handleModalConfirm = () => {
    if (!tempSelectedMap) return;

    let relativePositionToUse = selectedPositionRelative;

    if (!relativePositionToUse) {
      relativePositionToUse = { x: 0.5, y: 0.5 };
      setSelectedPositionRelative(relativePositionToUse);
    }

    setSelectedPosition(relativePositionToUse);
    setSelectedMap(tempSelectedMap);
    modalDispatch({ type: 'CLOSE_MAP_MODAL' });

    if (timerRef.current) {
      clearInterval(timerRef.current);
    }

    const mapWidth = mapData[tempSelectedMap]?.width || 100;
    const mapHeight = mapData[tempSelectedMap]?.height || 100;

    const positionToSubmit = {
      x: relativePositionToUse.x * mapWidth,
      y: relativePositionToUse.y * mapHeight
    };

    submitPosition(
      gameState.challengeId,
      tempSelectedMap,
      positionToSubmit,
      gameState.sessionId,
      timeRemaining
    ).then(result => {
      const updatedScores = [...gameState.scores, result.score];
      updateGameState({ scores: updatedScores });
      setRoundResult(result);
      setShowResults(true);
      triggerConfetti(result.score);
    }).catch(error => {
      console.error('Error submitting position:', error);
      showError(t('game.errorSubmittingPosition', 'Error submitting position. Please try again.'));
    });
  };

  // Handle submit button click
  // This function is defined but not currently used in the component
  // Keeping it commented out for potential future use
  /*
  const handleSubmit = useCallback(async (mapOverride = null) => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
    }

    // Check if mapData is empty
    if (Object.keys(mapData).length === 0) {
      console.error('No map data available');
      // Try to load map data again
      try {
        const maps = await fetchMapData();
        setMapData(maps);

        // Check if we successfully loaded map data
        if (Object.keys(maps).length === 0) {
          console.error('Failed to load map data for submission');
          // Show an error message or return to main screen
          showError(t('game.errorLoadingMapData', 'Error loading map data. Please try again.'));
          return; // Don't proceed if no map data
        }
      } catch (error) {
        console.error('Error loading map data:', error);
        showError(t('game.errorLoadingMapData', 'Error loading map data. Please try again.'));
        return; // Don't proceed if error loading map data
      }
    }

    // Prepare the map and position values to use in the API call
    // Use overrides if provided, otherwise use state values
    let mapToSubmit = mapOverride !== null ? mapOverride : selectedMap;
    let positionToSubmit;
    let relativePositionToUse;

    if (selectedPositionRelative) {
      // If we have a relative position, convert it to absolute pixel coordinates for API
      // Get map dimensions from mapData
      const mapWidth = mapData[mapToSubmit]?.width || 100;
      const mapHeight = mapData[mapToSubmit]?.height || 100;

      // Convert relative coordinates (0-1 range) to absolute pixel coordinates
      positionToSubmit = {
        x: selectedPositionRelative.x * mapWidth,
        y: selectedPositionRelative.y * mapHeight
      };
      relativePositionToUse = selectedPositionRelative;
    } else {
      // If no selection, use a default position
      mapToSubmit = Object.keys(mapData)[0] || '';
      relativePositionToUse = { x: 0.5, y: 0.5 }; // Center in 0-1 range

      // Get map dimensions from mapData
      const mapWidth = mapData[mapToSubmit]?.width || 100;
      const mapHeight = mapData[mapToSubmit]?.height || 100;

      // Convert relative coordinates (0-1 range) to absolute pixel coordinates
      positionToSubmit = { 
        x: relativePositionToUse.x * mapWidth, 
        y: relativePositionToUse.y * mapHeight 
      };

      // Also update the state for UI consistency
      setSelectedMap(mapToSubmit);
      setSelectedPosition({ 
        x: relativePositionToUse.x * 100, 
        y: relativePositionToUse.y * 100 
      });
      setSelectedPositionRelative(relativePositionToUse);
    }

    try {
      // Make sure we're sending valid values to the API
      if (!gameState.challengeId || !mapToSubmit || !positionToSubmit || !positionToSubmit.x || !positionToSubmit.y) {
        console.error('Invalid data for submission:', { 
          gameId: gameState.challengeId, 
          map: mapToSubmit, 
          position: positionToSubmit 
        });
        showError(t('game.errorSubmittingPosition', 'Error submitting position. Missing required data.'));
        return;
      }

      const result = await submitPosition(
        gameState.challengeId,
        mapToSubmit,
        positionToSubmit,
        gameState.sessionId,
        timeRemaining
      );

      // Check if time expired
      if (result.timeExpired) {
        // Create a result object with 0 points
        const timeExpiredResult = {
          score: 0,
          distance: -1, // -1 to hide the line and actual position
          timeExpired: true
        };

        // Update scores in game state with 0 points
        const updatedScores = [...gameState.scores, 0];
        const stateUpdate = { scores: updatedScores };

        updateGameState(stateUpdate);

        // Set time expired flag
        setTimeExpired(true);

        // Set round result and show results
        setRoundResult(timeExpiredResult);
        setShowResults(true);
        return;
      }

      // Update scores in game state
      const updatedScores = [...gameState.scores, result.score];
      const stateUpdate = { scores: updatedScores };

      updateGameState(stateUpdate);

      // Set round result and show results in one render cycle
      setRoundResult(result);
      setShowResults(true);

      // Trigger confetti for high scores
      triggerConfetti(result.score);
    } catch (error) {
      console.error('Error submitting position:', error);
      showError(t('game.errorSubmittingPosition', 'Error submitting position. Please try again.'));
    }
  }, [
    timerRef,
    mapData,
    selectedMap,
    selectedPosition,
    selectedPositionRelative,
    gameState,
    t,
    setMapData,
    setSelectedMap,
    setSelectedPosition,
    triggerConfetti, 
    setSelectedPositionRelative, 
    setRoundResult, 
    setShowResults, 
    setTimeExpired, 
    updateGameState
  ]);
  */

  // Total rounds: daily challenge uses its own round count, normal game uses 5
  const totalRounds = gameState.dailyRoundCount || 5;

  // Handle next round button click
  const handleNextRound = async () => {
    // Check if this is the final round FIRST before resetting anything
    if (gameState.currentRound >= totalRounds) {
      // Game is over, finalize session - don't reset challenge state
      finalizeSession();
      return;
    }

    // Clear challenge cache to ensure a fresh challenge is loaded for the next round
    clearApiCache('challenge');

    setShowResults(false);
    setSelectedMap('');
    setSelectedPosition(null);
    setSelectedPositionRelative(null);
    setRoundResult(null);
    setTimeRemaining(60);
    setTimeExpired(false);

    // Reset challengeLoaded flag to ensure a new challenge is loaded for the next round
    loadingDispatch({ type: 'SET_CHALLENGE_LOADED', payload: false });

    // Reset challengeId to ensure a new challenge is loaded for the next round
    updateGameState({ challengeId: null });

    // Check if map data is available
    if (mapKeys.length === 0) {
      console.warn('No map data available for next round, attempting to load map data');
      // Try to load map data again
      try {
        const maps = await fetchMapData();
        setMapData(maps);

        // Only proceed if we successfully loaded map data
        if (Object.keys(maps).length === 0) {
          console.error('Failed to load map data for next round');
          onGameEnd();
          return;
        }
      } catch (error) {
        console.error('Error loading map data for next round:', error);
        // If we can't load map data, return to main screen
        handleGameEnd();
        return;
      }
    }

    // Start next round
    updateGameState({ currentRound: gameState.currentRound + 1 });
  };

  // Finalize game session
  const finalizeSession = async () => {
    try {
      // Daily challenge: submit to daily endpoint (scores persisted in DB after each round)
      if (gameState.dailyChallengeId) {
        const res = await fetch('/api/challenges/daily/submit', {
          method: 'POST',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            challengeId: gameState.dailyChallengeId
          })
        });

        if (res.ok) {
          const result = await res.json();
          setRoundResult({
            isFinal: true,
            totalScore: result.totalScore || gameState.scores.reduce((a, b) => a + b, 0),
            rank: result.rank,
            isDailyChallenge: true,
            newAchievements: result.newAchievements || []
          });
        } else {
          setRoundResult({
            isFinal: true,
            totalScore: gameState.scores.reduce((a, b) => a + b, 0),
            isDailyChallenge: true,
            newAchievements: []
          });
        }

        modalDispatch({ type: 'SHOW_FINAL_SCORE' });
        return;
      }

      const finalResult = await finalizeGameSession(gameState.sessionId);
      setRoundResult({ ...finalResult, isFinal: true });
      modalDispatch({ type: 'SHOW_FINAL_SCORE' });
    } catch (error) {
      console.error('Error finalizing session:', error);
      handleGameEnd();
    }
  };

  // Format time remaining
  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs < 10 ? '0' : ''}${secs}`;
  };

  // Show error modal with a message and optional callback
  const showError = useCallback((message, callback = null) => {
    modalDispatch({ type: 'SHOW_ERROR', payload: { message, callback } });
  }, []);

  // Handle error modal close
  const handleErrorModalClose = useCallback(() => {
    const callback = errorCallback;
    modalDispatch({ type: 'CLOSE_ERROR' });
    if (callback) {
      callback();
    }
  }, [errorCallback]);

  // State for volume dropdown visibility
  const [showVolumeControl, setShowVolumeControl] = useState(false);

  // Toggle session mute state
  const toggleSessionMute = () => {
    if (globalVolume === 0) {
      // If volume is 0, set it to 100 when unmuting
      setGlobalVolume(100);
      setGlobalMuted(false);
    } else {
      // Toggle mute state
      setGlobalMuted(prev => !prev);
    }
  };

  // Handle volume change
  const handleVolumeChange = (e) => {
    const newVolume = parseInt(e.target.value, 10);
    setGlobalVolume(newVolume);

    // If volume is set to 0, mute the audio
    // If volume is changed from 0, unmute the audio
    if (newVolume === 0) {
      setGlobalMuted(true);
    } else if (globalMuted && newVolume > 0) {
      setGlobalMuted(false);
    }
  };

  // Toggle volume control visibility
  const toggleVolumeControl = (e) => {
    e.stopPropagation(); // Prevent the click from triggering the mute toggle
    setShowVolumeControl(prev => !prev);
  };

  // Close volume control when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      // Check if the click is outside the volume control container
      const volumeContainer = document.querySelector('.volume-control-container');
      if (showVolumeControl && volumeContainer && !volumeContainer.contains(e.target)) {
        setShowVolumeControl(false);
      }
    };

    // Add event listener when dropdown is shown
    if (showVolumeControl) {
      document.addEventListener('click', handleClickOutside);
    }

    // Clean up the event listener
    return () => {
      document.removeEventListener('click', handleClickOutside);
    };
  }, [showVolumeControl]);

  // State for keyboard coordinate input (accessibility feature)
  const [keyboardCoords, setKeyboardCoords] = useState({ x: '', y: '' });

  // Handle keyboard coordinate input change
  const handleKeyboardCoordChange = (axis, value) => {
    // Update the keyboard coords state
    setKeyboardCoords(prev => ({
      ...prev,
      [axis]: value
    }));

    // If both coordinates are valid numbers between 0-100, update the position
    const newCoords = {
      ...keyboardCoords,
      [axis]: value
    };

    const xVal = parseFloat(newCoords.x);
    const yVal = parseFloat(newCoords.y);

    if (!isNaN(xVal) && !isNaN(yVal) &&
        xVal >= 0 && xVal <= 100 &&
        yVal >= 0 && yVal <= 100) {
      // Convert percentage (0-100) to relative position (0-1)
      const relativePos = {
        x: xVal / 100,
        y: yVal / 100
      };

      setSelectedPositionRelative(relativePos);
      setSelectedPosition(relativePos);
    }
  };

  // Reset keyboard coords when modal opens
  useEffect(() => {
    if (showMapModal) {
      // If there's an existing position, populate the inputs
      if (selectedPositionRelative) {
        setKeyboardCoords({
          x: (selectedPositionRelative.x * 100).toFixed(1),
          y: (selectedPositionRelative.y * 100).toFixed(1)
        });
      } else {
        setKeyboardCoords({ x: '', y: '' });
      }
    }
  }, [showMapModal, selectedPositionRelative]);

  return (
    <div className="game-screen" role="main" aria-label={t('game.gameScreen', 'Game Screen')}>
      <header className="game-header" role="banner">
        <div className="round-info" aria-label={t('game.roundInfo', 'Round information')}>
          <span className="round-label">{t('game.round')}</span>
          <span className="round-number" aria-current="step">{gameState.currentRound}</span>
          <span className="round-total">/ {totalRounds}</span>
        </div>

        <div className="timer" role="timer" aria-live="polite" aria-label={t('game.timeRemaining', 'Time remaining')}>
          <i className="fas fa-clock" aria-hidden="true"></i>
          <span className="time-remaining">{formatTime(timeRemaining)}</span>
        </div>

        <div className="score-display" aria-label={t('game.currentScore', 'Current score')}>
          <span className="score-label">{t('game.score')}</span>
          <span className="score-value" aria-live="polite">
            {totalScore}
          </span>
        </div>

        <button
          className="report-btn"
          onClick={() => setShowReportModal(true)}
          aria-label={t('report.button', 'Report an issue')}
          title={t('report.button', 'Report an issue')}
        >
          <i className="fas fa-flag" aria-hidden="true"></i>
        </button>
      </header>

      <div className={`game-content ${showResults ? 'results-shown' : ''} ${showResults && roundResult?.isFinal ? 'final-state' : ''} ${gameLayout === 'split' ? 'game-content-split' : gameLayout === 'classic' ? 'game-content-classic' : 'game-content-modal'}`}>
        {loading ? (
          <div className="loading-container" role="status" aria-busy="true" aria-live="polite">
            <div className="loading-spinner" aria-hidden="true"></div>
            <p>{t('game.loading')}</p>
          </div>
        ) : (
          <>
            {showResults && gameLayout === 'modal' && (
              <div className="results-container top-results">
                <div className="result-details">
                  <h3>{t('game.roundResult')}</h3>
                  <div className="result-stats">
                    <div className="result-stat">
                      <span className="stat-label">{t('game.selectedMap')}</span>
                      <span className="stat-value">{mapData[selectedMap]?.name || selectedMap}</span>
                    </div>
                    <div className="result-stat">
                      <span className="stat-label">{t('game.score')}</span>
                      <span className="stat-value">{roundResult?.score || 0}</span>
                    </div>
                    <div className="result-stat">
                      <span className="stat-label">{t('game.distance')}</span>
                      <span className="stat-value">
                        {roundResult?.distance !== -1 ? `${roundResult?.distance || 0} ${t('game.pixels')}` : ''}
                      </span>
                    </div>
                    <div className="result-stat">
                      <span className="stat-label">{t('game.currentRound')}</span>
                      <span className="stat-value">{gameState.currentRound} / {totalRounds}</span>
                    </div>
                    <div className="result-stat">
                      <span className="stat-label">{t('game.totalScore')}</span>
                      <span className="stat-value">
                        {totalScore}
                      </span>
                    </div>
                  </div>
                  <p className="location-description">
                    {timeExpired
                      ? t('game.timeExpiredScore', 'Time expired. Your score for this round is 0 points.')
                      : (roundResult?.actualPosition?.description || t('game.unknownLocation'))}
                  </p>
                  {!roundResult?.isFinal && (
                    <button
                      className="btn btn-primary next-round-btn"
                      onClick={handleNextRound}
                    >
                      {t('game.nextRoundButton')}
                    </button>
                  )}
                </div>
                <Advertisement zone="results-screen" className="game-results-ad" />
              </div>
            )}
            <div className="challenge-container">
              {(!showResults || gameLayout !== 'modal') && (
                <div className="challenge-image-container">
                  <img
                    src={gameState.challengeImage}
                    alt={t('game.challengeImage')}
                    className="challenge-image"
                  />
                </div>
              )}

              <div className={`map-selection ${gameLayout === 'classic' && tempSelectedMap && !showResults ? 'classic-map-active' : ''}`}>
                {gameLayout === 'split' && tempSelectedMap && !showResults ? (
                  /* Split mode: inline map view (no modal) */
                  <div className="split-inline-map">
                    <div className="split-inline-header">
                      <button
                        className="split-back-btn"
                        onClick={handleModalCancel}
                        aria-label={t('game.cancel')}
                      >
                        <i className="fas fa-arrow-left" aria-hidden="true"></i>
                      </button>
                      <span className="split-map-name">{mapData[tempSelectedMap]?.name || tempSelectedMap}</span>
                      <span className="split-timer" aria-live="polite">{formatTime(timeRemaining)}</span>
                    </div>
                    <ZoomableMap
                      src={`/images/maps/${tempSelectedMap}.webp`}
                      alt={mapData[tempSelectedMap]?.name || tempSelectedMap}
                      onPositionSelect={handlePositionSelect}
                      className="game-map split-map"
                    >
                      {selectedPositionRelative && (
                        <GuessMarker position={selectedPositionRelative} />
                      )}
                    </ZoomableMap>
                    <div className="split-inline-actions">
                      <button
                        className="btn btn-secondary"
                        onClick={handleModalCancel}
                      >
                        {t('game.cancel')}
                      </button>
                      <button
                        className="btn btn-primary"
                        onClick={handleModalConfirm}
                      >
                        {t('game.confirm')}
                      </button>
                    </div>
                  </div>
                ) : gameLayout === 'split' && showResults ? (
                  /* Split mode: results in sidebar */
                  <div className="split-results-panel">
                    <div className="split-results-stats">
                      <h3>{t('game.roundResult')}</h3>
                      <div className="result-stats">
                        <div className="result-stat">
                          <span className="stat-label">{t('game.selectedMap')}</span>
                          <span className="stat-value">{mapData[selectedMap]?.name || selectedMap}</span>
                        </div>
                        <div className="result-stat">
                          <span className="stat-label">{t('game.score')}</span>
                          <span className="stat-value">{roundResult?.score || 0}</span>
                        </div>
                        <div className="result-stat">
                          <span className="stat-label">{t('game.distance')}</span>
                          <span className="stat-value">
                            {roundResult?.distance !== -1 ? `${roundResult?.distance || 0} ${t('game.pixels')}` : ''}
                          </span>
                        </div>
                        <div className="result-stat">
                          <span className="stat-label">{t('game.currentRound')}</span>
                          <span className="stat-value">{gameState.currentRound} / {totalRounds}</span>
                        </div>
                        <div className="result-stat">
                          <span className="stat-label">{t('game.totalScore')}</span>
                          <span className="stat-value">{totalScore}</span>
                        </div>
                      </div>
                      <p className="location-description">
                        {timeExpired
                          ? t('game.timeExpiredScore', 'Time expired. Your score for this round is 0 points.')
                          : (roundResult?.actualPosition?.description || t('game.unknownLocation'))}
                      </p>
                      {!roundResult?.isFinal && (
                        <button
                          className="btn btn-primary next-round-btn"
                          onClick={handleNextRound}
                        >
                          {t('game.nextRoundButton')}
                        </button>
                      )}
                    </div>
                    <div className="selected-map-result">
                      <ZoomableMap
                        src={`/images/maps/${selectedMap}.webp`}
                        alt={mapData[selectedMap]?.name || selectedMap}
                        disabled={true}
                        className="game-map"
                      >
                        {selectedPositionRelative && (
                          <GuessMarker position={selectedPositionRelative} />
                        )}
                        {roundResult && roundResult.distance !== -1 && roundResult.actualPosition && (
                          <>
                            <ActualMarker
                              position={{
                                x: roundResult.actualPosition.x / mapData[selectedMap]?.width,
                                y: roundResult.actualPosition.y / mapData[selectedMap]?.height,
                              }}
                            />
                            {selectedPositionRelative && (
                              <DistanceLine
                                from={selectedPositionRelative}
                                to={{
                                  x: roundResult.actualPosition.x / mapData[selectedMap]?.width,
                                  y: roundResult.actualPosition.y / mapData[selectedMap]?.height,
                                }}
                              />
                            )}
                          </>
                        )}
                      </ZoomableMap>
                    </div>
                    <Advertisement zone="results-screen" className="game-results-ad" />
                  </div>
                ) : gameLayout === 'classic' && tempSelectedMap && !showResults ? (
                  /* Classic mode: inline map in floating panel (expands on hover) */
                  <div className="classic-inline-map">
                    <div className="classic-inline-header">
                      <button
                        className="classic-back-btn"
                        onClick={handleModalCancel}
                        aria-label={t('game.cancel')}
                      >
                        <i className="fas fa-arrow-left" aria-hidden="true"></i>
                      </button>
                      <span className="classic-map-name">{mapData[tempSelectedMap]?.name || tempSelectedMap}</span>
                      <span className="classic-timer">{formatTime(timeRemaining)}</span>
                    </div>
                    <ZoomableMap
                      src={`/images/maps/${tempSelectedMap}.webp`}
                      alt={mapData[tempSelectedMap]?.name || tempSelectedMap}
                      onPositionSelect={handlePositionSelect}
                      className="game-map classic-map"
                    >
                      {selectedPositionRelative && (
                        <GuessMarker position={selectedPositionRelative} />
                      )}
                    </ZoomableMap>
                    <div className="classic-inline-actions">
                      <button className="btn btn-secondary btn-sm" onClick={handleModalCancel}>
                        {t('game.cancel')}
                      </button>
                      <button className="btn btn-primary btn-sm" onClick={handleModalConfirm}>
                        {t('game.confirm')}
                      </button>
                    </div>
                  </div>
                ) : gameLayout === 'classic' && showResults ? (
                  /* Classic mode: full-width results panel */
                  <div className="classic-results-panel">
                    <div className="classic-results-stats">
                      <h3>{t('game.roundResult')}</h3>
                      <div className="result-stats">
                        <div className="result-stat">
                          <span className="stat-label">{t('game.selectedMap')}</span>
                          <span className="stat-value">{mapData[selectedMap]?.name || selectedMap}</span>
                        </div>
                        <div className="result-stat">
                          <span className="stat-label">{t('game.score')}</span>
                          <span className="stat-value">{roundResult?.score || 0}</span>
                        </div>
                        <div className="result-stat">
                          <span className="stat-label">{t('game.distance')}</span>
                          <span className="stat-value">
                            {roundResult?.distance !== -1 ? `${roundResult?.distance || 0} ${t('game.pixels')}` : ''}
                          </span>
                        </div>
                        <div className="result-stat">
                          <span className="stat-label">{t('game.currentRound')}</span>
                          <span className="stat-value">{gameState.currentRound} / {totalRounds}</span>
                        </div>
                        <div className="result-stat">
                          <span className="stat-label">{t('game.totalScore')}</span>
                          <span className="stat-value">{totalScore}</span>
                        </div>
                      </div>
                      <p className="location-description">
                        {timeExpired
                          ? t('game.timeExpiredScore', 'Time expired. Your score for this round is 0 points.')
                          : (roundResult?.actualPosition?.description || t('game.unknownLocation'))}
                      </p>
                      {!roundResult?.isFinal && (
                        <button
                          className="btn btn-primary next-round-btn"
                          onClick={handleNextRound}
                        >
                          {t('game.nextRoundButton')}
                        </button>
                      )}
                    </div>
                    <div className="classic-results-map">
                      <ZoomableMap
                        src={`/images/maps/${selectedMap}.webp`}
                        alt={mapData[selectedMap]?.name || selectedMap}
                        disabled={true}
                        className="game-map"
                      >
                        {selectedPositionRelative && (
                          <GuessMarker position={selectedPositionRelative} />
                        )}
                        {roundResult && roundResult.distance !== -1 && roundResult.actualPosition && (
                          <>
                            <ActualMarker
                              position={{
                                x: roundResult.actualPosition.x / mapData[selectedMap]?.width,
                                y: roundResult.actualPosition.y / mapData[selectedMap]?.height,
                              }}
                            />
                            {selectedPositionRelative && (
                              <DistanceLine
                                from={selectedPositionRelative}
                                to={{
                                  x: roundResult.actualPosition.x / mapData[selectedMap]?.width,
                                  y: roundResult.actualPosition.y / mapData[selectedMap]?.height,
                                }}
                              />
                            )}
                          </>
                        )}
                      </ZoomableMap>
                    </div>
                    <Advertisement zone="results-screen" className="game-results-ad" />
                  </div>
                ) : !selectedMap || (selectedMap && !showResults) || (showResults && timeExpired) ? (
                  gameLayout === 'modal' ? (
                    /* Modal mode: map thumbnails with previews (like multiplayer) */
                    <div className="modal-map-selector" role="group" aria-label={t('game.selectMap')}>
                      {mapKeys.map(mapId => (
                        <button
                          key={mapId}
                          className={`modal-map-thumb ${selectedMap === mapId ? 'modal-map-thumb-selected' : ''}`}
                          onClick={() => handleMapSelect(mapId)}
                          disabled={showResults && timeExpired}
                          aria-pressed={selectedMap === mapId}
                          aria-label={t('game.selectMapNamed', { map: mapData[mapId]?.name || mapId }, `Select ${mapData[mapId]?.name || mapId}`)}
                        >
                          <img
                            src={`/images/maps/${mapId}.webp`}
                            alt={mapData[mapId]?.name || mapId}
                            draggable={false}
                          />
                          <span>{mapData[mapId]?.name || mapId}</span>
                        </button>
                      ))}
                    </div>
                  ) : (
                    /* Split/Classic mode: text buttons */
                    <>
                      <h3 id="map-selection-heading">{t('game.selectMap')}</h3>
                      <div className="map-buttons" role="group" aria-labelledby="map-selection-heading">
                        {mapKeys.map(mapId => (
                          <button
                            key={mapId}
                            className={`map-button ${selectedMap === mapId ? 'selected' : ''}`}
                            onClick={() => handleMapSelect(mapId)}
                            disabled={showResults && timeExpired}
                            aria-pressed={selectedMap === mapId}
                            aria-label={t('game.selectMapNamed', { map: mapData[mapId]?.name || mapId }, `Select ${mapData[mapId]?.name || mapId}`)}
                          >
                            {mapData[mapId]?.name || mapId}
                          </button>
                        ))}
                      </div>
                    </>
                  )
                ) : (
                  <div className="selected-map-result">
                    <ZoomableMap
                      src={`/images/maps/${selectedMap}.webp`}
                      alt={mapData[selectedMap]?.name || selectedMap}
                      onPositionSelect={!showResults ? handlePositionSelect : undefined}
                      disabled={showResults}
                      className="game-map"
                    >
                      {selectedPositionRelative && (
                        <GuessMarker position={selectedPositionRelative} />
                      )}
                      {showResults && roundResult && roundResult.distance !== -1 && roundResult.actualPosition && (
                        <>
                          <ActualMarker
                            position={{
                              x: roundResult.actualPosition.x / mapData[selectedMap]?.width,
                              y: roundResult.actualPosition.y / mapData[selectedMap]?.height,
                            }}
                          />
                          {selectedPositionRelative && (
                            <DistanceLine
                              from={selectedPositionRelative}
                              to={{
                                x: roundResult.actualPosition.x / mapData[selectedMap]?.width,
                                y: roundResult.actualPosition.y / mapData[selectedMap]?.height,
                              }}
                            />
                          )}
                        </>
                      )}
                    </ZoomableMap>
                  </div>
                )}
              </div>
            </div>
          </>
        )}
      </div>

      <div className="game-controls">
        {showResults && roundResult?.isFinal && !showFinalScoreModal && (
          <button 
            className="btn btn-primary play-again-btn"
            onClick={handleGameEnd}
          >
            {t('game.playAgainButton')}
          </button>
        )}
      </div>

      {/* Map Selection Modal */}
      {showMapModal && (
        <div className="map-modal-overlay" role="presentation">
          <div
            className="map-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="map-modal-title"
            ref={mapModalFocusTrapRef}
            tabIndex={-1}
          >
            <div className="map-modal-header">
              <h3 id="map-modal-title">
                <span>{t('game.selectPosition')}</span>
                <span className="time-in-title" aria-live="polite">{formatTime(timeRemaining)}</span>
              </h3>
              <button
                className="modal-close-btn"
                onClick={handleModalCancel}
                aria-label={t('game.closeModal', 'Close modal')}
              >
                <i className="fas fa-times" aria-hidden="true"></i>
              </button>
            </div>
            <div className="map-modal-content">
              <ZoomableMap
                src={`/images/maps/${tempSelectedMap}.webp`}
                alt={mapData[tempSelectedMap]?.name || tempSelectedMap}
                onPositionSelect={handlePositionSelect}
                className="game-map-modal"
              >
                {selectedPositionRelative && (
                  <GuessMarker position={selectedPositionRelative} />
                )}
              </ZoomableMap>
            </div>
            <div className="map-modal-footer">
              <div className="modal-buttons">
                <button
                  className="btn btn-secondary"
                  onClick={handleModalCancel}
                >
                  {t('game.cancel')}
                </button>
                <button
                  className="btn btn-primary"
                  onClick={handleModalConfirm}
                >
                  {t('game.confirm')}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Error Modal */}
      {showErrorModal && (
        <div className="map-modal-overlay" role="presentation">
          <div
            className="map-modal"
            role="alertdialog"
            aria-modal="true"
            aria-labelledby="error-modal-title"
            aria-describedby="error-modal-message"
            ref={errorModalFocusTrapRef}
            tabIndex={-1}
          >
            <div className="map-modal-header">
              <h3 id="error-modal-title">{t('game.error', 'Error')}</h3>
              <button
                className="modal-close-btn"
                onClick={handleErrorModalClose}
                aria-label={t('game.closeModal', 'Close modal')}
              >
                <i className="fas fa-times" aria-hidden="true"></i>
              </button>
            </div>
            <div className="map-modal-content">
              <p id="error-modal-message" className="error-message" role="alert">{errorMessage}</p>
            </div>
            <div className="map-modal-footer">
              <button
                className="btn btn-primary"
                onClick={handleErrorModalClose}
              >
                {t('game.ok', 'OK')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Final Score Modal */}
      {showFinalScoreModal && (
        <div className="map-modal-overlay" role="presentation">
          <div
            className="map-modal final-score-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="final-score-modal-title"
            ref={finalScoreModalFocusTrapRef}
            tabIndex={-1}
          >
            <div className="map-modal-header">
              <h3 id="final-score-modal-title">{t('game.finalScore', 'Final Score')}</h3>
              <button
                className="modal-close-btn"
                onClick={handleGameEnd}
                aria-label={t('game.closeModal', 'Close modal')}
              >
                <i className="fas fa-times" aria-hidden="true"></i>
              </button>
            </div>
            <div className="map-modal-content">
              <div className="final-score-container">
                <div className="final-score">
                  <h4>{t('game.totalScore', 'Total Score')}</h4>
                  <span className="final-score-value">
                    {totalScore}
                  </span>
                  {roundResult?.isDailyChallenge && roundResult?.rank && (
                    <span className="daily-rank-badge">
                      <i className="fas fa-trophy"></i> #{roundResult.rank}
                    </span>
                  )}
                </div>

                <div className="rounds-scores">
                  <h4>{t('game.roundScores', 'Round Scores')}</h4>
                  {gameState.scores.map((score, index) => (
                    <div key={index} className="round-score-item">
                      <div className="round-info">
                        <span className="round-label">{t('game.round')} {index + 1}</span>
                        <span className="round-score">{score}</span>
                      </div>
                      <div className="score-progress-container">
                        <div
                          className="score-progress-bar"
                          style={{
                            width: `${Math.min(100, (score / 5000) * 100)}%`,
                            backgroundColor: `hsl(${Math.min(120, (score / 5000) * 120)}, 100%, 45%)`
                          }}
                        ></div>
                      </div>
                    </div>
                  ))}
                </div>

                {/* New Achievements Section */}
                {roundResult?.newAchievements && roundResult.newAchievements.length > 0 && (
                  <div className="new-achievements-section">
                    <h4>{t('game.newAchievements', 'Achievements Unlocked!')}</h4>
                    <div className="achievements-list">
                      {roundResult.newAchievements.map((achievement, index) => (
                        <div key={index} className={`achievement-item rarity-${achievement.rarity || 'common'}`}>
                          <div className="achievement-icon">
                            <i className={`fas ${achievement.icon || 'fa-trophy'}`} aria-hidden="true"></i>
                          </div>
                          <div className="achievement-info">
                            <span className="achievement-name">
                              {t(achievement.nameKey, achievement.code)}
                            </span>
                            <span className="achievement-description">
                              {t(achievement.descriptionKey, '')}
                            </span>
                          </div>
                          {achievement.points > 0 && (
                            <div className="achievement-points">
                              +{achievement.points}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
            <div className="map-modal-footer">
              <button
                className="btn btn-primary"
                onClick={handleGameEnd}
              >
                {t('game.playAgain', 'Play Again')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Report Modal */}
      {showReportModal && (
        <ReportModal
          onClose={() => setShowReportModal(false)}
          context={{
            pointId: gameState.currentPointId || null,
            mapId: gameState.currentMapId || null,
            sessionId: gameState.sessionId || null,
            mapName: selectedMap ? (mapData[selectedMap]?.name || selectedMap) : null
          }}
        />
      )}

      {/* Floating mute button with volume control */}
      <div className="volume-control-container" role="group" aria-label={t('game.audioControls', 'Audio controls')}>
        <button
          className={`floating-mute-button ${globalMuted ? 'muted' : ''}`}
          onClick={toggleSessionMute}
          title={globalMuted ? t('game.unmute', 'Unmute for this session') : t('game.mute', 'Mute for this session')}
          aria-pressed={globalMuted}
          aria-label={globalMuted ? t('game.unmute', 'Unmute for this session') : t('game.mute', 'Mute for this session')}
        >
          <i className={`fas ${globalMuted ? 'fa-volume-mute' : globalVolume > 66 ? 'fa-volume-up' : globalVolume > 33 ? 'fa-volume-down' : 'fa-volume-off'}`} aria-hidden="true"></i>
          <i
            className="fas fa-caret-down volume-caret"
            onClick={toggleVolumeControl}
            role="button"
            aria-label={t('game.toggleVolumeControl', 'Toggle volume control')}
            aria-expanded={showVolumeControl}
            aria-hidden="true"
          ></i>
        </button>

        {showVolumeControl && (
          <div className="volume-dropdown" role="group" aria-label={t('game.volumeSlider', 'Volume slider')}>
            <div className="volume-title" id="volume-slider-label">{t('game.volumeControl', 'Volume Control')}</div>
            <input
              type="range"
              min="0"
              max="100"
              value={globalVolume}
              onChange={handleVolumeChange}
              className="volume-slider"
              aria-labelledby="volume-slider-label"
              aria-valuenow={globalVolume}
              aria-valuemin={0}
              aria-valuemax={100}
            />
            <div className="volume-value" aria-live="polite">{globalVolume}</div>
          </div>
        )}
      </div>
    </div>
  );
};

export default GameScreen;
